import { useCallback, useState, useContext } from "react";
// utils
import { isIMGiftBagItem, getIMItemDescription } from "@cart-client/helpers/GiftingIMSubCart";
import { isEmpty } from "@swiggy-private/core-ui";
import { useGiftCartItemActions } from "./useGiftCartItemActions";
// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
// components
import { Item } from "@swiggy-private/dls-web-recipe-common-web/dist/components/ReviewItemListCard";
// types
import {
    CombinedCartInStockItemRender,
    GiftingCartItemType,
    IMStructureItem,
    GiftBagSelectionMode,
    FoodCartItemWithUUID,
    IMCartItemWithUUID,
} from "@swiggy-private/cart-types";
import { GiftingIMGiftBagHalfcardProps } from "@swiggy-private/cart-ui";
import { GiftingCartState } from "@cart-client/reducers/GiftingCartReducer";

interface ItemAdditionHelperProps {
    allSubCarts: CombinedCartInStockItemRender[];
    item: Item;
    mode?: GiftBagSelectionMode;
}

export const useIMGiftBagHalfCardSelection = ({
    foodCartItemList,
    imCartItemList,
}: {
    foodCartItemList?: FoodCartItemWithUUID[];
    imCartItemList?: IMCartItemWithUUID[];
}) => {
    const [
        giftBagSelectionHalfCardProps,
        setGiftBagSelectionHalfCardProps,
    ] = useState<GiftingIMGiftBagHalfcardProps | null>(null);
    const { addGiftBagItem, removeGiftBagItem } = useGiftCartItemActions({
        foodCartItemList: foodCartItemList || [],
        imCartItemList: imCartItemList || [],
    });
    const { cartState } = useContext(CartContext);
    const { reviewedCart } = cartState as GiftingCartState;
    const { imGiftBag } = reviewedCart?.data || {};

    const filterItemsToShowInHalfCard = useCallback((items?: IMStructureItem[]) => {
        if (isEmpty(items)) {
            return [];
        }
        return items?.map(item => ({
            title: item?.name ?? "",
            subtitle: getIMItemDescription(item),
            imageId: item?.imageIds?.[0] ?? "",
        }));
    }, []);

    const onCloseHandler = useCallback(() => {
        setGiftBagSelectionHalfCardProps(null);
    }, []);

    const getSubCartWithGiftBag = useCallback((allSubCarts: CombinedCartInStockItemRender[], item: Item) => {
        return allSubCarts.find(cart => cart.subCartId === item.parentCartId);
    }, []);

    const getGiftBagItemFromSubCart = useCallback(
        (allSubCarts: CombinedCartInStockItemRender[], item: Item) => {
            const subCart = getSubCartWithGiftBag(allSubCarts, item);
            const giftBagItem = subCart?.giftBagItems?.find(giftBagItem => {
                return giftBagItem.variations.some(variation => variation.id === item.id);
            });
            return giftBagItem;
        },
        [getSubCartWithGiftBag],
    );

    const ctaClickHandler = useCallback(
        async ({ allSubCarts, item, mode }: ItemAdditionHelperProps) => {
            const giftBagItem = getGiftBagItemFromSubCart(allSubCarts, item);
            if (!isEmpty(giftBagItem)) {
                const isGiftBagPackagingAllowed = mode === GiftBagSelectionMode.IN_THE_BAG;
                await addGiftBagItem({
                    imGiftBagItemsToAdd: [giftBagItem],
                    useGiftBagPackaging: isGiftBagPackagingAllowed,
                });
                onCloseHandler();
            }
        },
        [addGiftBagItem, getGiftBagItemFromSubCart, onCloseHandler],
    );

    const onGiftBagAddition = useCallback(
        ({ item, allSubCarts }: ItemAdditionHelperProps) => {
            if (!isIMGiftBagItem(item.subType as GiftingCartItemType)) {
                return;
            }

            const parentCart = getSubCartWithGiftBag(allSubCarts, item);
            if (isEmpty(parentCart)) {
                return;
            }

            setGiftBagSelectionHalfCardProps({
                isOpen: true,
                items: filterItemsToShowInHalfCard(parentCart?.items as IMStructureItem[]),
                onCloseHandler,
                onPrimaryClickHandler: () =>
                    ctaClickHandler({ allSubCarts, item, mode: GiftBagSelectionMode.IN_THE_BAG }),
                onSecondaryClickHandler: () =>
                    ctaClickHandler({ allSubCarts, item, mode: GiftBagSelectionMode.OUT_OF_BAG }),
                popupDetails: imGiftBag?.widgetInfo?.popupDetails,
            });
        },
        [
            ctaClickHandler,
            filterItemsToShowInHalfCard,
            getSubCartWithGiftBag,
            imGiftBag?.widgetInfo?.popupDetails,
            onCloseHandler,
        ],
    );

    const onGiftBagRemoval = useCallback(
        async ({ item }: Omit<ItemAdditionHelperProps, "allSubCarts">) => {
            if (!isEmpty(item)) {
                await removeGiftBagItem({
                    imGiftBagItemsToRemove: [{ itemId: item.id }],
                });
            }
        },
        [removeGiftBagItem],
    );

    return {
        onGiftBagAddition,
        onGiftBagRemoval,
        giftBagSelectionHalfCardProps,
    };
};
